import { Component, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { inflate } from 'zlib';

import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import { ActivityHistory, Job } from '../models';

import { allJobFields, allHoldBinFields, allRfidProductionFields } from '../../ngrx/graphql';
import { HttpClient } from '@angular/common/http';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class JobService {
  displayJobDetails        : Subject<any> = new Subject();
  S3  : AWS.S3;

  constructor(
    private http             : HttpClient,
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private store            : Store<any>,
  ) {}

  private invokeJobs(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.magentoFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err.stack);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
              if(output.gzip){
                const options = {
                  windowBits : 15
                };
                inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                  if (err) {                    
                    observer.error(err.stack);
                  } else {
                    const unzippedOutput = Object.assign({}, output, {
                      body: data.toString()
                    });
                    observer.next(unzippedOutput);
                  }
                });
              } else {
                observer.next(output);
              }
          }
        }
      );
    });
  }

  private invokeJobMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.magentoFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
        }
      }
    );
  }

  getHoldListView(): Observable<any> {
    return this.store.select('holds', 'holdListView');
  }

  getJob(id): Observable<any> {
    const query = `{
      job(id: "${id}") {
        ${allJobFields}
      }
    }`;

    return this.invokeJobs({ query });
  }

  getJobDetailsView(): Observable<any> {
    return this.store.select('jobs', 'jobDetailsView');
  }

  getJobList(): Observable<Job[]> {
    return this.store.select('jobs', 'jobList');
  }

  getGHSList(): Observable<Job[]> {
    return this.store.select('jobs', 'ghsList');
  }

  getStickerList(): Observable<Job[]> {
    return this.store.select('jobs', 'stickerList');
  }
  
  getJobListView(): Observable<any> {
    return this.store.select('jobs', 'jobListView');
  }

  getQueuedMTSJobList(): Observable<Job[]> {
    return this.store.select('jobs', 'queuedMTSJobList');
  }

  getJobShipFlagView(): Observable<any> {
    return this.store.select('jobs', 'jobShipFlagView');
  }

  getHoldBinList(): Observable<any> {
    return this.store.select('holdBins', 'holdBinList');
  }

  getHoldBinListView(): Observable<any> {
    return this.store.select('holdBins', 'holdBinsView');
  }


  getJobsByID(ids: string[]): Observable<any> {
    let formattedIDs = '';
    ids.forEach(id => {
      formattedIDs += `"${id}", `;
    });

    const query = `{
      jobsByID(ids: [${formattedIDs.trim()}]) {
        ${allJobFields}
      }
    }`;

    return this.invokeJobs({ query });
  }

  updateJob(job: Job): void {
    const variables = { job };
    const query = `mutation($job: JobInput!) {
      updateJob(job: $job) {
        ${allJobFields}
      }
    }`;

    this.invokeJobMutation({ query, variables });
  }

  updateJobs(jobs: Job[]): void {
    const variables = { jobs };
    const query = `mutation($jobs: [JobInput]) {
      updateJobs(jobs: $jobs) {
        ${allJobFields}
      }
    }`;

    this.invokeJobMutation({ query, variables });
  }

  createJobActivityRecord(jobId: string, activityRecord: ActivityHistory): void {
    const variables = { id: jobId, activityRecord };
    const query = `mutation CreateJobActivityRecord($id: ID!, $activityRecord: ActivityRecordInput!) {
      createJobActivityRecord(id: $id, activityRecord: $activityRecord) {
        ${allJobFields}
      }
    }`;

    this.invokeJobMutation({ query, variables });
  }

  //Secret create Qa bins
  createHoldBins(category: string): void {
    const variables = { category };
    const query = `mutation($category: String) {
      createHoldBins(category: $category) {
        ${allHoldBinFields}
      }
    }`;

    this.invokeJobMutation({ query, variables });
  }

  getRFIDInnerCasePDF(jobKey: string): Promise<string> {
    const bucketParams = {
      Bucket: environment.rfidInnerCaseLabelBucket,
      Key: jobKey,
    };
  
    return new Promise((resolve, reject) => {
      // Check if the S3 object exists
      this.credentialService.s3.headObject(bucketParams, (err) => {
        if (err) {
          if (err.code === 'NotFound') {
            console.error('S3 object not found:', jobKey);
            reject(new Error(`S3 object not found for key: ${jobKey}`));
          } else {
            console.error('Error checking S3 object:', err);
            reject(new Error('Error verifying S3 object existence'));
          }
          return;
        }
  
        // Generate a signed URL if the object exists
        this.credentialService.s3.getSignedUrl('getObject', bucketParams, (err, url) => {
          if (err) {
            console.error('Error generating signed URL:', err);
            reject(new Error('Unable to generate signed URL for the PDF'));
          } else {
            resolve(url);
          }
        });
      });
    });
  }  

  recordPaperSheetConsumption(jobId: string, transitionId: string, consumption: any): Observable<any> {
    const variables = { jobId, transitionId, consumption };
    const query = `mutation($jobId: String!, $transitionId: String!, $consumption: PaperSheetConsumptionInput!) {
      recordPaperSheetConsumption(jobId: $jobId, transitionId: $transitionId, consumption: $consumption) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeRfidProductionQuery({ query, variables });
  }

  recordInlaySheetConsumption(jobId: string, transitionId: string, consumption: any): Observable<any> {
    const variables = { jobId, transitionId, consumption };
    const query = `mutation($jobId: String!, $transitionId: String!, $consumption: InlaySheetConsumptionInput!) {
      recordInlaySheetConsumption(jobId: $jobId, transitionId: $transitionId, consumption: $consumption) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeRfidProductionQuery({ query, variables });
  }

  recordLaminateConsumption(jobId: string, transitionId: string, consumption: any): Observable<any> {
    const variables = { jobId, transitionId, consumption };
    const query = `mutation($jobId: String!, $transitionId: String!, $consumption: LaminateConsumptionInput!) {
      recordLaminateConsumption(jobId: $jobId, transitionId: $transitionId, consumption: $consumption) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeRfidProductionQuery({ query, variables });
  }

  getRfidProduction(jobId: string): Observable<any> {
    const variables = { jobId };
    const query = `query($jobId: String!) {
      getRfidProduction(jobId: $jobId) {
        ${allRfidProductionFields}
      }
    }`;

    return this.invokeRfidProductionQuery({ query, variables });
  }

  private invokeRfidProductionQuery(payload: any): Observable<any> {
    return new Observable(observer => {
      this.credentialService.lambda.invoke({
        FunctionName: environment.magentoFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                : data.Payload;
            
            if (output.isBase64Encoded) {
              const options = {
                level: 9
              };
              inflate(new Buffer(output.body, 'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
            observer.complete();
          }
        }
      );
    });
  }

  async getPresignedUrl(bucket: string, key: string) {
    const bucketParams = {
      Bucket: bucket,
      Key: key,
    };
  
    try {
      const headResponse = await this.credentialService.s3.headObject(bucketParams).promise();
      // console.log(headResponse);
      
      // Generate a signed URL if the object exists
      const response = await this.credentialService.s3.getSignedUrl('getObject', bucketParams);
      // console.log(response);
      return response;
    } catch(err) {
      console.log(err);
      if(err.code === "Forbidden") {
        console.log('S3 object not found:', key);
      }
    }
    return null;
  }

  public downloadArtworks(jobId: any) {
    return this.http.get(`${environment.magentoServiceApiUrl}/jobs/${jobId}/redownload-artwork`);
  }
}


// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/services/job.service.ts